/**
 * Putting the shop's chosen look on the screen.
 *
 * Two settings: light or dark (or "follow the device"), and one accent colour.
 * Everything else is arithmetic - see `accentShades` in the shared package.
 *
 * "Follow the device" is resolved HERE into a literal light or dark and written
 * to `data-theme` on <html>. That is why the stylesheet defines the dark palette
 * exactly once, instead of repeating it for a `prefers-color-scheme` block that
 * would then have to be kept in step by hand.
 *
 * The last choice is also mirrored into localStorage, so the tiny script in
 * index.html can paint the right background on the very first frame. Without it
 * a dark till flashes white every time it opens, which looks broken and, at
 * 7am in a shop, is genuinely unpleasant.
 */
import { accentShades, DEFAULT_ACCENT, type ThemeMode } from '@pos/shared';

export type ThemeChoice = 'auto' | 'light' | 'dark';

const CACHE_KEY = 'pos.theme';

const media = () =>
  typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

/** What "auto" means on this device, right now. */
export function resolveMode(choice: ThemeChoice): ThemeMode {
  if (choice === 'light' || choice === 'dark') return choice;
  return media()?.matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice, accent: string): ThemeMode {
  const mode = resolveMode(choice);
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);

  const s = accentShades(accent || DEFAULT_ACCENT, mode);
  root.style.setProperty('--accent', s.accent);
  root.style.setProperty('--accent-hover', s.hover);
  root.style.setProperty('--accent-soft', s.soft);
  root.style.setProperty('--accent-ink', s.ink);
  root.style.setProperty('--on-accent', s.onAccent);

  // The browser chrome around an installed app - phone status bar, title bar.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', s.accent);

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ choice, accent, mode }));
  } catch {
    /* a locked-down browser; the theme still works, it just flashes on boot */
  }
  return mode;
}

/**
 * While the shop is set to "follow the device", the device can change its mind -
 * at sunset, or when someone flips the switch in iOS Control Centre.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const m = media();
  if (!m) return () => undefined;
  const handler = () => onChange();
  // Safari below 14 has only the deprecated form.
  if (m.addEventListener) m.addEventListener('change', handler);
  else m.addListener(handler);
  return () => {
    if (m.removeEventListener) m.removeEventListener('change', handler);
    else m.removeListener(handler);
  };
}
